/** Authentication API: session bootstrap, local login, providers, logout. */

import { ApiError, apiFetch, setCsrfToken } from "./client";

/** The authenticated session, as returned by `/api/auth/me` and login. */
export interface SessionUser {
  user_id: number;
  display_name: string;
  email: string | null;
  sip_user: string | null;
  permissions: string[];
  csrf_token: string;
  /** Server idle window (minutes); drives the pre-expiry warning. */
  idle_minutes: number;
}

/** A configured auth provider offered on the login page. */
export interface AuthProvider {
  id: number;
  name: string;
  provider_type: string;
}

/** List enabled auth providers (public — for the login page). */
export function getProviders(): Promise<AuthProvider[]> {
  return apiFetch<AuthProvider[]>("/api/auth/providers");
}

/**
 * Return the current session, or `null` if not authenticated.
 *
 * On success the CSRF token is registered for subsequent mutations.
 */
export async function getCurrentSession(): Promise<SessionUser | null> {
  try {
    const session = await apiFetch<SessionUser>("/api/auth/me");
    setCsrfToken(session.csrf_token);
    return session;
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthenticated) return null;
    throw error;
  }
}

/** Authenticate a local account; returns the new session. */
export async function loginLocal(username: string, password: string): Promise<SessionUser> {
  const session = await apiFetch<SessionUser>("/api/auth/login/local", {
    method: "POST",
    body: { username, password },
  });
  setCsrfToken(session.csrf_token);
  return session;
}

/** Revoke the current session (CSRF-checked server-side). */
export async function logout(): Promise<void> {
  await apiFetch<{ status: string }>("/api/auth/logout", { method: "POST" });
  setCsrfToken(null);
}
